import { AxiosError, AxiosResponse } from "axios";
import publicApi from "./publicApi";
import {
  BusinessProposalData,
  buildBusinessProposalUrl,
  normalizeBusinessProposalFromApi,
} from "../lib/businessProposalParams";

export interface BusinessOnboardingCompleteResult {
  subscription_id?: number;
  status?: string;
  payment_intent_id?: string;
  next_charge_at?: string;
  amount_cents?: number;
  currency?: string;
  billing_cycle?: string;
}

export interface CompleteBusinessOnboardingPayload {
  payment_method: string;
  plan_id?: number;
  type: "monthly" | "yearly";
  stripe_email?: string;
  customer_name?: string;
  billing_country?: string;
  accepted_terms: boolean;
  accepted_privacy: boolean;
}

function getResponseErrorMessage(response: AxiosResponse): string | null {
  if (response.data?.status === false) {
    return response.data?.message || "Request failed.";
  }
  return null;
}

export function getBusinessProposalErrorMessage(error: unknown): string {
  if (error instanceof AxiosError) {
    return (
      error.response?.data?.message ||
      error.message ||
      "Unable to load business onboarding."
    );
  }

  if (error instanceof Error) {
    return error.message;
  }

  return "Unable to load business onboarding.";
}

function assertSuccessfulResponse(response: AxiosResponse, fallbackMessage: string) {
  const message = getResponseErrorMessage(response);
  if (message) {
    throw new Error(message);
  }

  const payload = response.data?.data ?? response.data;
  if (payload == null) {
    throw new Error(fallbackMessage);
  }

  return payload;
}

/**
 * GET /api/business-onboarding/{subs-id-with-encoded}
 */
export async function getBusinessProposal(
  encodedSubscriptionId: string,
): Promise<BusinessProposalData> {
  const response = await publicApi.get(
    `/business-onboarding/${encodeURIComponent(encodedSubscriptionId)}`,
  );

  const payload = assertSuccessfulResponse(
    response,
    "Business onboarding details not found.",
  );

  if (!payload || typeof payload !== "object") {
    throw new Error("Business onboarding details not found.");
  }

  const proposal = normalizeBusinessProposalFromApi(
    payload as Record<string, unknown>,
    encodedSubscriptionId,
  );

  if (!proposal.packageName) {
    throw new Error("Business onboarding data is incomplete.");
  }

  return proposal;
}

export async function createBusinessProposalSetupIntent(encodedSubscriptionId: string) {
  const response = await publicApi.post(
    `/business-onboarding/${encodeURIComponent(encodedSubscriptionId)}/setup-intent`,
  );

  return assertSuccessfulResponse(response, "Unable to start secure card setup.");
}

export async function completeBusinessProposalPayment(
  encodedSubscriptionId: string,
  payload: CompleteBusinessOnboardingPayload,
): Promise<BusinessOnboardingCompleteResult> {
  const response = await publicApi.post(
    `/business-onboarding/${encodeURIComponent(encodedSubscriptionId)}/complete`,
    payload,
  );

  return assertSuccessfulResponse(response, "Payment could not be completed.");
}

export { buildBusinessProposalUrl };
